'use client';
import React, { createContext, useContext, useCallback, useState, ReactNode } from "react";

type ToastType = "success" | "error" | "warning" | "info";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

const ToastContext = createContext<((message: string, options?: { type?: ToastType; duration?: number }) => void) | undefined>(undefined);

let toastId = 0;

const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (
      message: string,
      options?: {
        type?: ToastType;
        duration?: number;
      }
    ) => {
      const id = `${Date.now()}-${toastId++}`;
      const type = options?.type || "info";
      const duration = options?.duration ?? 2000;
      setToasts((prev) => [...prev, { id, message, type, duration }]);
      setTimeout(() => removeToast(id), duration);
    },
    [removeToast]
  );

  return (
    <ToastContext value={showToast}>
      {children}
      <div
        className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center pointer-events-none"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext>
  );
};

function useToast() {
  const showToast = useContext(ToastContext);
  if (!showToast) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return showToast;
}

const toastTypeClasses: Record<ToastType, string> = {
  success: "bg-teal-100 text-teal-800 border-teal-400",
  error: "bg-red-100 text-red-700 border-red-400",
  warning: "bg-yellow-100 text-yellow-800 border-yellow-400",
  info: "bg-blue-100 text-blue-700 border-blue-400",
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  return (
    <div
      className={`min-w-[240px] mb-3 border-l-4 rounded-md shadow-md px-5 py-3 text-base font-medium pointer-events-auto flex items-center gap-3 relative ${toastTypeClasses[toast.type]}`}
      role="alert"
    >
      <span>{toast.message}</span>
      <button
        onClick={onClose}
        className="ml-auto bg-transparent border-none text-inherit text-lg cursor-pointer"
        aria-label="Close"
        type="button"
      >
        ×
      </button>
    </div>
  );
};

export { ToastProvider, useToast };
